import React from "react";
import {useSearchParams} from "react-router";
import type {GridSortModel} from "@mui/x-data-grid-premium";
import type {ClContractLifecycle} from "./contractExplorerTypes.ts";
import {LIFECYCLES} from "./contractExplorerTypes.ts";

/**
 * The list's search / status filter / sort, kept in the URL.
 *
 * Both the list page and the detail sidebar read them from here, so the two
 * always show the same set of contracts, going back to the list restores what
 * you were looking at, and a filtered list can be shared as a link.
 */
export type ContractListParams = {
    search: string;
    lifecycle: ClContractLifecycle | null;
    /** Cleaned contract type (a value from the summary's `type_counts`). */
    type: string | null;
    /** The API's `ordering`: a snake_case key from `ORDERING_KEYS`, `-` prefix = descending. */
    ordering: string;
    /**
     * Contract shown in the side panel. A query param on purpose: changing it
     * does not remount the route, so the grid (and its page, sort and
     * selection) survives opening, switching and closing.
     */
    contract: number | null;
    /** Zero-based page of the list, as the grid counts them. */
    page: number;
    pageSize: number;
};

export const DEFAULT_ORDERING = '-contracted_amount';
export const DEFAULT_PAGE_SIZE = 50;
export const PAGE_SIZE_OPTIONS = [25, 50, 100];

/** The sort keys the API accepts (DRF `OrderingFilter`), without the `-` prefix. */
export const ORDERING_KEYS = [
    'contract_title',
    'supplier_name',
    'contract_type',
    'effective_date',
    'expiration_date',
    'contracted_amount',
    'id',
] as const;

export type OrderingKey = typeof ORDERING_KEYS[number];

/** True for `key` / `-key` with a key the API knows. Anything else — including
 *  the old camelCase aliases in bookmarked links — falls back to the default. */
export function isValidOrdering(value: string | null | undefined): value is string {
    if (!value) return false;
    const key = value.startsWith('-') ? value.slice(1) : value;
    return (ORDERING_KEYS as readonly string[]).includes(key);
}

/** Grid column field -> API ordering key. Columns not listed here are not sortable. */
export const SORT_FIELD_TO_ORDERING: Record<string, OrderingKey> = {
    title: 'contract_title',
    supplier: 'supplier_name',
    type: 'contract_type',
    effectiveDate: 'effective_date',
    expirationDate: 'expiration_date',
    contractedAmount: 'contracted_amount',
};

const ORDERING_TO_SORT_FIELD: Record<string, string> = Object.fromEntries(
    Object.entries(SORT_FIELD_TO_ORDERING).map(([field, key]) => [key, field]),
);

/** Grid sort model -> the API's `ordering`. An empty model (sort cleared) means the default. */
export function toOrdering(sortModel: GridSortModel): string {
    const [sort] = sortModel;
    if (!sort) return DEFAULT_ORDERING;
    const key = SORT_FIELD_TO_ORDERING[sort.field];
    if (!key) return DEFAULT_ORDERING;
    return `${sort.sort === 'desc' ? '-' : ''}${key}`;
}

/** The API's `ordering` -> grid sort model, for the sort indicator. Empty for a
 *  key without a column (`id`): the list is still ordered by it, the grid just
 *  has nothing to point at. */
export function toSortModel(ordering: string): GridSortModel {
    const value = isValidOrdering(ordering) ? ordering : DEFAULT_ORDERING;
    const desc = value.startsWith('-');
    const field = ORDERING_TO_SORT_FIELD[desc ? value.slice(1) : value];
    return field ? [{field, sort: desc ? 'desc' : 'asc'}] : [];
}

export function readContractListParams(params: URLSearchParams): ContractListParams {
    const state = params.get('lifecycle');
    const ordering = params.get('ordering');
    return {
        search: params.get('search') ?? '',
        lifecycle: LIFECYCLES.includes(state as ClContractLifecycle) ? (state as ClContractLifecycle) : null,
        type: params.get('type'),
        ordering: isValidOrdering(ordering) ? ordering : DEFAULT_ORDERING,
        contract: Number(params.get('contract')) || null,
        // 1-based in the URL (what a user reads), 0-based everywhere else.
        page: Math.max((Number(params.get('page')) || 1) - 1, 0),
        pageSize: PAGE_SIZE_OPTIONS.includes(Number(params.get('page_size')))
            ? Number(params.get('page_size'))
            : DEFAULT_PAGE_SIZE,
    };
}

export function useContractListParams() {
    const [searchParams, setSearchParams] = useSearchParams();
    const params = React.useMemo(() => readContractListParams(searchParams), [searchParams]);

    const update = React.useCallback((patch: Partial<ContractListParams>) => {
        setSearchParams(previous => {
            const next = new URLSearchParams(previous);
            const merged = {...readContractListParams(previous), ...patch};
            // Narrowing the result set invalidates the page number: page 7 of
            // 883 contracts is nowhere in 25 filtered ones. Centralised here so
            // no call site can forget it (an explicit page in the patch wins).
            const narrowing: (keyof ContractListParams)[] = ['search', 'lifecycle', 'type', 'ordering'];
            if (patch.page === undefined && narrowing.some(key => key in patch)) merged.page = 0;
            const write = (key: string, value: string | null, omitWhen?: string) => {
                if (!value || value === omitWhen) next.delete(key);
                else next.set(key, value);
            };
            write('search', merged.search);
            write('lifecycle', merged.lifecycle);
            write('type', merged.type);
            write('ordering', merged.ordering, DEFAULT_ORDERING);
            write('contract', merged.contract ? String(merged.contract) : null);
            write('page', merged.page > 0 ? String(merged.page + 1) : null);
            write('page_size', String(merged.pageSize), String(DEFAULT_PAGE_SIZE));
            return next;
            // Filter changes replace rather than stack, so Back leaves the page
            // instead of walking every keystroke.
        }, {replace: true});
    }, [setSearchParams]);

    return {params, update};
}
